import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { AssetsRelatedEntity } from '@app/core/model/entities/asset/assets-related-entity';
import { Document } from '@app/core/model/entities/document/document';
import { Entity, IRelatedEntity, RelatedEntity } from '@app/core/model/entities/entity';
import { getValue, hasProperty, setValue } from '@app/shared/extra/utils';
import { Expose, Type } from 'class-transformer';

export class Asset extends Entity implements AssetsRelatedEntity {
  public objectType = EntityTypeEnum.ASSET;

  public identifier: number;
  public order: number;
  public organizationId: string;
  public typeId: string;

  @Type(() => Document) public pictures: Document[];
  public assetState: string;
  public dataDate: string;

  @Expose()
  public toString(): string {
    return this.computedProperties?.['displayName'] ?? `${this?.identifier} - ${this?.name}`;
  }

  public toRelatedAsset(): RelatedAsset {
    return new RelatedAsset({
      id: this.id,
      typeId: this.typeId,
      typeModules: this.computedProperties?.['typeModules'],
      name: this.name,
      identifier: this.identifier,
      displayName: this.computedProperties?.['displayName']
    });
  }

  /**
   * Convert the asset to a list of related assets.
   * @return List of related assets.
   */
  @Expose()
  public get relatedAssets(): RelatedAsset[] {
    return [this.toRelatedAsset()];
  }

  protected static inputMapping = {
    type: {input: ['type', 'id'], output: 'typeId'},
  };

  /**
   * Some GraphQL inputs require different keys than those available in the Form State Service
   * since those keys correspond to field codes, so we need to replace them accordingly
   * @param obj the Graphql input object
   */
  public static transformPropertiesForInput(obj: Record<string, unknown>): void {
    // Transform input, mapping keys as defined in the inputMapping above
    for (const inputMappingElement in Asset.inputMapping) {
      if (hasProperty(obj, Asset.inputMapping[inputMappingElement].input)) {
        const source = getValue(obj, Asset.inputMapping[inputMappingElement].input);
        setValue(obj, [Asset.inputMapping[inputMappingElement].output], source);
      }
    }

    // Clean up by removing older keys
    for (const inputMappingElement in Asset.inputMapping) {
      delete obj[Asset.inputMapping[inputMappingElement].input.first()];
    }
  }
}

/**
 * AssetInput for creating or updating Assets.
 */
export interface AssetInput {
  name?: string;
  assetState?: string;
  typeId?: string;
  properties?: Record<string, any>;
  dataDate?: string;
}

export type IRelatedAsset = IRelatedEntity & { typeId: string, typeModules: string[] };

/**
 * Class used by entities that have asset's data in their model data
 */
export class RelatedAsset extends RelatedEntity implements IRelatedAsset {
  public typeId: string;
  public typeModules: string[];

  constructor(relatedAsset: IRelatedAsset) {
    super(relatedAsset);
    this.typeId = relatedAsset.typeId;
    this.typeModules = relatedAsset.typeModules;
    this.displayName = relatedAsset.displayName
      ?? `${relatedAsset.identifier ?? '?'} - ${relatedAsset.name ?? '?'}`;
  }

  @Expose()
  public toString(): string {
    return this.displayName;
  }
}
